/**
 * The locale set and what each locale implies. `LOC-001`, `LOC-002`.
 *
 * `LOC-001` `MUST`: "Full UI translation with RTL support (Arabic, Urdu). RTL is a layout
 * requirement, not a font swap."
 *
 * `LOC-002` `MUST`: "Per-tenant and per-user language. Counter staff and owner may differ."
 *
 * **Two locales ship: English and Urdu.** `LOC-001` names Arabic as well, but Appendix S's
 * Localization paragraph says "Urdu and English at v1", and no Arabic string has been written or
 * reviewed. `ar` is therefore not in {@link LOCALES}, so nothing can select it, while
 * {@link directionOf} still answers `rtl` for it: a customer's `preferred_language` (`CUS-001`) is
 * free BCP-47 text and a document renderer must not lay an Arabic customer's copy out left to
 * right just because the UI has no Arabic catalogue.
 */
export const LOCALES = ['en', 'ur'];
/**
 * English is the default and the fallback. It is the one catalogue that is complete by
 * construction (the type of every other catalogue is derived from it), so it is the only language
 * a missing string can safely degrade to.
 */
export const DEFAULT_LOCALE = 'en';
/**
 * Primary language subtags written right to left. A list rather than a per-{@link Locale} table so
 * that `directionOf('ur-PK')` and `directionOf('ar')` answer without a catalogue existing.
 */
const RTL_LANGUAGES = new Set([
    'ar',
    'ur',
    'fa',
    'ps',
    'sd',
    'ug',
    'he',
    'yi',
    'dv',
]);
const primarySubtag = (tag) => tag.trim().toLowerCase().split(/[-_]/, 1)[0] ?? '';
/** The direction a BCP-47 tag is written in. Anything unrecognised is `ltr`. */
export function directionOf(tag) {
    return RTL_LANGUAGES.has(primarySubtag(tag)) ? 'rtl' : 'ltr';
}
/**
 * A BCP-47 tag to one of the locales this product ships, or null. `ur-PK` and `ur-IN` are both
 * `ur`: the regional variants differ in the digits ICU defaults to (`ur-IN` uses Arabic-Indic),
 * and this product forbids those everywhere (`D-03`), so a region carries no information it may
 * act on.
 */
export function parseLocale(tag) {
    if (tag === null || tag === undefined)
        return null;
    const primary = primarySubtag(tag);
    return LOCALES.includes(primary) ? primary : null;
}
/**
 * **The one place a language is decided.** The staff member's own setting wins over the shop's,
 * which wins over {@link DEFAULT_LOCALE}.
 *
 * Order is `LOC-002`'s: the shop sets a default and each person may differ from it. A tag that is
 * present but not a locale this product ships falls through to the next source rather than
 * failing, so a column that one day holds `fr` degrades to the shop's language, not to a crash.
 */
export function resolveLocale(sources = {}) {
    return parseLocale(sources.user) ?? parseLocale(sources.tenant) ?? DEFAULT_LOCALE;
}
//# sourceMappingURL=locales.js.map